import { db } from '../firebase/config';
import { getSecuritySettings } from '../firebase/services/securityService';

// Constants
const AUTH_SESSION_KEY = 'authSession';
const USER_ID_KEY = 'currentUserId';
const USER_ROLE_KEY = 'userRole';
const AUTH_LOGOUT_FLAG = 'justLoggedOut';

// Types
interface AuthSession {
  timestamp: number;
  isValid: boolean;
  roleId?: string;
}

// Helper functions
export const isAuthSessionValid = async (): Promise<boolean> => {
  // Check for explicit logout flag first
  if (localStorage.getItem(AUTH_LOGOUT_FLAG) === 'true') {
    return false;
  }

  const session = getAuthSession();
  if (!session) return false;

  const now = Date.now();
  const sessionAge = now - session.timestamp;
  
  // Get security settings
  const securitySettings = await getSecuritySettings();
  
  // Determine session duration based on role and settings
  let sessionDuration = 0;
  if (session.roleId && securitySettings.sessionTimeouts && session.roleId in securitySettings.sessionTimeouts) {
    sessionDuration = securitySettings.sessionTimeouts[session.roleId] * 60 * 1000; // Convert minutes to ms
  }
  
  // If timeout is 0, session never expires
  if (sessionDuration === 0) {
    return session.isValid;
  }
  
  return session.isValid && sessionAge < sessionDuration;
};

export const updateAuthSession = (isValid: boolean, roleId?: string): void => {
  // Clear logout flag if explicitly setting session as valid
  if (isValid) {
    localStorage.removeItem(AUTH_LOGOUT_FLAG);
  }
  
  const session: AuthSession = {
    timestamp: Date.now(),
    isValid,
    roleId: roleId || getAuthSession()?.roleId
  };
  localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
};

export const clearAuthSession = async (): Promise<void> => {
  // Set logout flag to ensure session is considered invalid immediately
  localStorage.setItem(AUTH_LOGOUT_FLAG, 'true');
  
  // Clear all auth-related data
  localStorage.removeItem(AUTH_SESSION_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(USER_ROLE_KEY);
  
  // Force any cached session to be invalidated by setting an invalid session
  const invalidSession: AuthSession = {
    timestamp: Date.now(),
    isValid: false
  };
  localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(invalidSession));
  
  notifyAuthStateChange();
};

// Create a function to explicitly flag a logged out state
export const setLoggedOutState = (): void => {
  localStorage.setItem(AUTH_LOGOUT_FLAG, 'true');
};

export const clearLoggedOutState = (): void => {
  localStorage.removeItem(AUTH_LOGOUT_FLAG);
};

// User ID management
export const setCurrentUserId = (userId: string): void => {
  localStorage.setItem(USER_ID_KEY, userId);
};

export const getCurrentUserId = (): string | null => {
  return localStorage.getItem(USER_ID_KEY);
};

// User role management
export const setUserRoleId = (roleId: string): void => {
  localStorage.setItem(USER_ROLE_KEY, roleId);
  // Also update the roleId in the auth session
  const session = getAuthSession();
  if (session) {
    updateAuthSession(session.isValid, roleId);
  }
};

export const getUserRoleId = (): string | null => {
  return localStorage.getItem(USER_ROLE_KEY);
};

// Private helper functions
export const getAuthSession = (): AuthSession | null => {
  // If logout flag is set, don't even try to get the session
  if (localStorage.getItem(AUTH_LOGOUT_FLAG) === 'true') {
    return null;
  }
  
  const sessionStr = localStorage.getItem(AUTH_SESSION_KEY);
  if (!sessionStr) return null;
  
  try {
    const session = JSON.parse(sessionStr);
    
    // Handle legacy session format
    if ('authenticated' in session) {
      const newSession: AuthSession = {
        timestamp: session.timestamp,
        isValid: session.authenticated,
        roleId: session.roleId
      };
      localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(newSession));
      return newSession;
    }
    
    // Ensure the session has all required properties
    if (!session.timestamp || typeof session.isValid !== 'boolean') {
      return null;
    }
    return session;
  } catch (error) {
    return null;
  }
};

// Notify all components about auth state changes
export const notifyAuthStateChange = (): void => {
  window.dispatchEvent(new CustomEvent('authStateChange'));
  window.dispatchEvent(new Event('storageUpdated'));
};

// Add immediate sync check function to prevent unauthorized access on initial page load
export const isProtectedPath = (path: string): boolean => {
  return path.startsWith('/staff') || path.startsWith('/admin');
};

export const isAuthSessionValidSync = (): boolean => {
  // First check: is there a logout flag?
  if (localStorage.getItem(AUTH_LOGOUT_FLAG) === 'true') {
    return false;
  }

  // Second check: is there a 'true' value in local storage for isPasscodeAuthenticated?
  const hasPasscodeAuth = localStorage.getItem('isPasscodeAuthenticated') === 'true';
  if (hasPasscodeAuth) {
    return true;
  }

  // Third check: Is there a valid auth session object?
  const sessionStr = localStorage.getItem(AUTH_SESSION_KEY);
  if (!sessionStr || sessionStr === 'undefined' || sessionStr === 'null') {
    return false;
  }

  try {
    const session = JSON.parse(sessionStr);

    // For immediate checks, verify the session has required properties
    if (!session || typeof session !== 'object') {
      return false;
    }

    if (!('isValid' in session) || session.isValid !== true) {
      return false;
    }

    if (!('timestamp' in session)) {
      return false;
    }

    // Check session age - temporary basic check
    const now = Date.now();
    const sessionAge = now - session.timestamp;
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours default

    if (sessionAge > maxAge) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

// Add function to force immediate redirect for protected routes
export const enforceAuthForProtectedRoutes = (): void => {
  const path = window.location.pathname;
  
  // Only check protected paths
  if (!isProtectedPath(path)) {
    return;
  }
  
  console.log('🔒 Protected route access check:', path);
  
  // Check immediate sync auth, for faster blocking
  const isAuth = isAuthSessionValidSync();
  console.log('🔑 Auth check result:', isAuth);
  
  if (!isAuth) {
    console.log('🚫 UNAUTHORIZED ACCESS BLOCKED - redirecting to auth page');
    // Save the path for return after login
    sessionStorage.setItem('authRedirectPath', path);
    // Redirect to auth
    window.location.href = '/auth';
  }
}; 